import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { collection, query, where, onSnapshot, doc, getDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { getAuth } from "firebase/auth";
import { db } from '../config/firebase';
import { avatars } from '../components/logInUserInformation/ProfileAvatar';
import '../styles/Dashboard.css';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import FullSidebar from '../components/dashboard/FullSidebar';
import DashboardAside from '../components/dashboard/DashboardAside';
import DashboardContent from '../components/dashboard/DashboardContent';
import Pager from '../components/dashboard/Pager';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';

// Prime React
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

const Dashboard = () => {
    const [selectedSidebarItem, setSelectedSidebarItem] = useState('Home');
    const [selectedHeaderItem, setSelectedHeaderItem] = useState('');
    const [headerItems, setHeaderItems] = useState([]);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 1081);
    const [isSidebarVisible, setIsSidebarVisible] = useState(window.innerWidth > 1081);
    const [isNotificationPanelVisible, setIsNotificationPanelVisible] = useState(false);
    const [userAvatar, setUserAvatar] = useState(null);
    const [hasNotifications, setHasNotifications] = useState(false);
    const [userCity, setUserCity] = useState('');
    const [userName, setUserName] = useState('');
    const [partnerInfo, setPartnerInfo] = useState(null);
    const [favorites, setFavorites] = useState([]);
    const [showAddFavoriteDialog, setShowAddFavoriteDialog] = useState(false);
    const [newFavoriteName, setNewFavoriteName] = useState('');
    const [isAddingFavorite, setIsAddingFavorite] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const notificationRef = useRef(null);
    const lastNavRef = useRef({ section: null, ts: 0 });
    const auth = getAuth();
    // Dashboard wallpaper handling
    const [wallpaperUrl, setWallpaperUrl] = useState(null);
    const wallpapersReq = useMemo(() => {
        try {
            // Load GIF wallpapers from animation folder
            return require.context('../assets/animation', false, /\.gif$/);
        } catch (e) {
            return null;
        }
    }, []);

    const resolveWallpaper = useCallback((fileName) => {
        if (!fileName || !wallpapersReq) return null;
        
        const key = `./${fileName}`;
        console.log('Attempting to resolve wallpaper with key:', key);
        try {
            const resolved = wallpapersReq(key);
            console.log('Successfully resolved wallpaper to:', resolved);
            return resolved;
        } catch (e) {
            console.error('Failed to resolve wallpaper:', fileName, e);
            return null;
        }
    }, [wallpapersReq]);

    useEffect(() => {
        // Try to fetch wallpaper from Firestore first
        let unsub = null;
        const authUser = getAuth().currentUser;
        if (authUser) {
            const userDocRef = doc(db, "UserInformation", authUser.uid);
            unsub = onSnapshot(userDocRef, (docSnapshot) => {
                let wallpaperName = '';
                if (docSnapshot.exists()) {
                    const data = docSnapshot.data();
                    if (data.dashboardWallpaper) {
                        wallpaperName = data.dashboardWallpaper;
                        localStorage.setItem('dashboardWallpaper', wallpaperName); // keep in sync
                    } else {
                        wallpaperName = localStorage.getItem('dashboardWallpaper') || '';
                    }
                } else {
                    wallpaperName = localStorage.getItem('dashboardWallpaper') || '';
                }
                const url = resolveWallpaper(wallpaperName);
                setWallpaperUrl(url);
            });
        } else {
            // fallback to localStorage if not logged in
            const stored = localStorage.getItem('dashboardWallpaper');
            setWallpaperUrl(resolveWallpaper(stored));
        }

        // Listen to custom event from Profile within same tab
        const onUpdated = (e) => {
            const name = e?.detail?.name || '';
            const url = resolveWallpaper(name);
            setWallpaperUrl(url);
        };
        window.addEventListener('frazza:wallpaperUpdated', onUpdated);

        // Listen cross-tab via storage event
        const onStorage = (ev) => {
            if (ev.key === 'dashboardWallpaper') {
                setWallpaperUrl(resolveWallpaper(ev.newValue));
            }
        };
        window.addEventListener('storage', onStorage);

        return () => {
            if (unsub) unsub();
            window.removeEventListener('frazza:wallpaperUpdated', onUpdated);
            window.removeEventListener('storage', onStorage);
        };
    }, [resolveWallpaper]);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 1081;
            setIsMobileView(mobile);
            setIsSidebarVisible(!mobile);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const q = query(collection(db, 'Notifications'), where('uid', '==', 1011));
        return onSnapshot(q, (querySnapshot) => {
            setHasNotifications(querySnapshot.docs.length > 0);
        });
    }, []);

    useEffect(() => {
        const userDocRef = doc(db, "UserInformation", auth.currentUser.uid);
        return onSnapshot(userDocRef, async (docSnapshot) => {
            if (docSnapshot.exists()) {
                const data = docSnapshot.data();
                const avatarId = data.avatar;
                const city = data.city;
                const inRelation = data.inRelation || false;
                const partnerUid = data.inRelationWith;

                if (avatarId) {
                    const avatar = avatars.find(a => a.id === avatarId);
                    setUserAvatar(avatar?.src);
                }

                setUserCity(city || "Unknown City");
                setUserName(data.userName || "Unknown Username");

                if (inRelation && partnerUid) {
                    const partnerDocRef = doc(db, "UserInformation", partnerUid);
                    const partnerSnapshot = await getDoc(partnerDocRef);
                    if (partnerSnapshot.exists()) {
                        setPartnerInfo(partnerSnapshot.data());
                    }
                } else {
                    setPartnerInfo(null);
                }

                if (data.favorites) {
                    // Sort favorites by createdAt in descending order (newest first)
                    const sortedFavorites = [...data.favorites].sort((a, b) => 
                        new Date(b.createdAt) - new Date(a.createdAt)
                    );
                    setFavorites(sortedFavorites);
                }
            }
        });
    }, [auth.currentUser.uid]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (notificationRef.current && !notificationRef.current.contains(event.target)) {
                setIsNotificationPanelVisible(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isNotificationPanelVisible]);

    const closeSidebar = useCallback(() => {
        if (isMobileView) {
            setIsSidebarVisible(false);
        }
    }, [isMobileView]);

    const saveLastVisitedItem = useCallback((section, item) => {
        localStorage.setItem(`lastVisited_${section}`, item);
    }, []);

    const getLastVisitedItem = useCallback((section) => {
        return localStorage.getItem(`lastVisited_${section}`) || null;
    }, []);

    const updateHeaderItems = useCallback((items, activeItem, sidebarLabel) => {
        setHeaderItems(items.map(item => ({
            label: item.label,
            className: item.label === activeItem ? 'active' : '',
            command: () => {
                setSelectedHeaderItem(item.label);
                saveLastVisitedItem(sidebarLabel.toLowerCase(), item.label);
                navigate(`/dashboard/${sidebarLabel}/${item.label.toLowerCase().replace(/ /g, "-")}`);
            }
        })));

        setSelectedHeaderItem(activeItem);
    }, [navigate, saveLastVisitedItem]);

    const handleDatesSection = useCallback((segments) => {
        const datesItems = [
            { label: 'Cooking' },
            { label: 'Movies' },
            { label: 'Questions' },
            { label: 'Minigames' }
        ];

        let activeHeader = 'Movies';
        if (segments.length >= 3) {
            const subSection = segments[2];
            if (subSection === 'cooking') activeHeader = 'Cooking';
            else if (subSection === 'questions') activeHeader = 'Questions';
            else if (subSection === 'minigames') activeHeader = 'Minigames';
            else if (subSection === 'chat') activeHeader = 'Chat';
        } else {
            const lastVisited = getLastVisitedItem('dates');
            if (lastVisited) {
                activeHeader = lastVisited;
            }
        }

        saveLastVisitedItem('dates', activeHeader);
        updateHeaderItems(datesItems, activeHeader, 'dates');
    }, [getLastVisitedItem, saveLastVisitedItem, updateHeaderItems]);

    const handleOrganizationSection = useCallback((segments) => {
        const orgItems = [
            { label: 'Notes' },
            { label: 'Calendar' },
            { label: 'ToDo List' },
            { label: 'Study' }
        ];

        let activeHeader = 'Calendar';
        if (segments.length >= 3) {
            const subSection = segments[2];
            if (subSection === 'todo-list') activeHeader = 'ToDo List';
            else if (subSection === 'notes') activeHeader = 'Notes';
            else if (subSection === 'study') activeHeader = 'Study';
        } else {
            const lastVisited = getLastVisitedItem('organization');
            if (lastVisited) {
                activeHeader = lastVisited;
            }
        }

        saveLastVisitedItem('organization', activeHeader);
        updateHeaderItems(orgItems, activeHeader, 'organization');
    }, [getLastVisitedItem, saveLastVisitedItem, updateHeaderItems]);

    const handleNotebookSection = useCallback(() => {
        const trackerItems = [{ label: 'Menstrual tracker' }];
        const activeHeader = 'Menstrual tracker';
        saveLastVisitedItem('tracker', activeHeader);
        updateHeaderItems(trackerItems, activeHeader, 'tracker');
    }, [saveLastVisitedItem, updateHeaderItems]);

    const handleRouteChange = useCallback((mainSection, segments) => {
        switch (mainSection) {
            case 'home':
                setSelectedSidebarItem('Home');
                setHeaderItems([]);
                break;
            case 'dates':
                setSelectedSidebarItem('Dates');
                handleDatesSection(segments);
                break;
            case 'organization':
                setSelectedSidebarItem('Organization');
                handleOrganizationSection(segments);
                break;
            case 'tracker':
                setSelectedSidebarItem('Tracker');
                handleNotebookSection();
                break;
            case 'profil':
                setSelectedSidebarItem('');
                setSelectedHeaderItem('Profil');
                setHeaderItems([]);
                break;
            default:
                break;
        }
    }, [handleDatesSection, handleNotebookSection, handleOrganizationSection]);

    useEffect(() => {
        const path = location.pathname;
        const segments = path.split('/').filter(Boolean);

        if (path === '/dashboard') {
            setSelectedSidebarItem('Home');
            setHeaderItems([]);
            return;
        }

        if (segments.length >= 2) {
            const mainSection = segments[1];
            handleRouteChange(mainSection, segments);
        }
    }, [location.pathname, handleRouteChange]);

    const navigateToSection = useCallback((section, defaultItem, items) => {
        const sectionKey = section.toLowerCase();
        // Debounce/ignore rapid repeated clicks on the same section
        const now = Date.now();
        if (lastNavRef.current.section === section && (now - lastNavRef.current.ts) < 700) {
            if (isMobileView) setIsSidebarVisible(false);
            return;
        }
        lastNavRef.current = { section, ts: now };

        // If this section is already selected, do not update header items or navigate.
        // This makes the action idempotent and prevents repeated clicks from
        // modifying the navbar/header state.
        // Exception: Allow navigation from profile page to any section
        if (selectedSidebarItem === section && !location.pathname.includes('/dashboard/profil')) {
            if (isMobileView) setIsSidebarVisible(false);
            return;
        }
        if (items) {
            const lastVisited = getLastVisitedItem(sectionKey);
            const activeItem = lastVisited || defaultItem;
            updateHeaderItems(items, activeItem, sectionKey);
        } else {
            setHeaderItems([]);
        }

        setSelectedSidebarItem(section);

        if (items) {
            const lastVisited = getLastVisitedItem(sectionKey);
            const targetItem = lastVisited || defaultItem;
            navigate(`/dashboard/${sectionKey}/${targetItem.toLowerCase().replace(/ /g, "-")}`);
        } else {
            // For Home, navigate to /dashboard
            if (sectionKey === 'home') {
                navigate('/dashboard');
            } else {
                navigate(`/dashboard/${sectionKey}`);
            }
        }

        if (isMobileView) {
            setIsSidebarVisible(false);
        }
    }, [updateHeaderItems, navigate, isMobileView, getLastVisitedItem, selectedSidebarItem, location.pathname]);

    const sidebarItems = useMemo(() => [
        {
            icon: 'pi pi-home',
            className: selectedSidebarItem === 'Home' ? 'active sidebar-item-selected' : 'sidebar-item',
            command: () => navigateToSection('Home')
        },
        {
            icon: 'pi pi-heart',
            className: selectedSidebarItem === 'Dates' ? 'active sidebar-item-selected' : 'sidebar-item',
            command: () => navigateToSection('Dates', 'Movies', [
                { label: 'Cooking' },
                { label: 'Movies' },
                { label: 'Questions' },
            ])
        },
        {
            icon: 'pi pi-objects-column',
            className: selectedSidebarItem === 'Organization' ? 'active sidebar-item-selected' : 'sidebar-item',
            command: () => navigateToSection('Organization', 'Calendar', [
                { label: 'Calendar' },
                { label: 'ToDo List' },
                { label: 'Notes' },
                { label: 'Study' }
            ])
        },
        {
            icon: 'pi pi-clipboard',
            className: selectedSidebarItem === 'Tracker' ? 'active sidebar-item-selected' : 'sidebar-item',
            command: () => navigateToSection('Tracker', 'Menstrual tracker', [
                { label: 'Menstrual tracker' }
            ])
        }
    ], [selectedSidebarItem, navigateToSection]);

    const toggleNotifications = useCallback(() => {
        setIsNotificationPanelVisible(prev => !prev);
    }, []);

    const navigateToProfile = useCallback(() => {
        setSelectedHeaderItem('Profil');
        navigate('/dashboard/profil');
        if (isMobileView) {
            setIsSidebarVisible(false);
        }
    }, [navigate, isMobileView]);

    const toggleSidebar = useCallback(() => {
        setIsSidebarVisible(prev => !prev);
    }, []);

    const getSidebarLabel = (iconClass) => {
        const iconMap = {
            'pi-home': 'Home',
            'pi-heart': 'Dates',
            'pi-objects-column': 'Organization',
            'pi-clipboard': 'Tracker'
        };

        const iconKey = Object.keys(iconMap).find(key => iconClass.includes(key));
        return iconKey ? iconMap[iconKey] : '';
    };

    const handleAddFavorite = async () => {
        if (!newFavoriteName.trim()) return;

        try {
            setIsAddingFavorite(true);
            const userUid = auth.currentUser.uid;
            const userRef = doc(db, 'UserInformation', userUid);

            // Get the full URL including search parameters
            const fullPath = window.location.pathname + window.location.search;
            console.log('Saving favorite with path:', fullPath); // Debug log

            const newFavorite = {
                name: newFavoriteName,
                path: fullPath,
                createdAt: new Date().toISOString()
            };

            await updateDoc(userRef, {
                favorites: arrayUnion(newFavorite)
            });

            setShowAddFavoriteDialog(false);
            setNewFavoriteName('');
        } catch (error) {
            console.error('Error adding favorite:', error);
        } finally {
            setIsAddingFavorite(false);
        }
    };

    const handleRemoveFavorite = async (favorite) => {
        try {
            const userUid = auth.currentUser.uid;
            const userRef = doc(db, 'UserInformation', userUid);

            await updateDoc(userRef, {
                favorites: arrayRemove(favorite)
            });
        } catch (error) {
            console.error('Error removing favorite:', error);
        }
    };

    const navigateToFavorite = (favorite) => {
        navigate(favorite.path);
        if (isMobileView) {
            setIsSidebarVisible(false);
        }
    };

    const mainBackgroundStyle = useMemo(() => {
        console.log('Computing mainBackgroundStyle - wallpaperUrl:', wallpaperUrl, 'selectedSidebarItem:', selectedSidebarItem);
        return wallpaperUrl && selectedSidebarItem === 'Home'
            ? {
                backgroundImage: `url(${wallpaperUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundColor: 'transparent'
            }
            : {};
    }, [wallpaperUrl, selectedSidebarItem]);

    const containerBackgroundStyle = useMemo(() => (
        wallpaperUrl && selectedSidebarItem === 'Home'
            ? {
                backgroundImage: `url(${wallpaperUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            }
            : {}
    ), [wallpaperUrl, selectedSidebarItem]);

    const containerClassName = useMemo(() => {
        const classes = ['dashboard-container'];
        if (selectedSidebarItem === 'Home') {
            classes.push('home-view');
        }
        if (wallpaperUrl && selectedSidebarItem === 'Home') {
            classes.push('home-has-wallpaper');
        }
        return classes.join(' ');
    }, [wallpaperUrl, selectedSidebarItem]);

    return (
        <div className={containerClassName} style={containerBackgroundStyle}>
            <DashboardHeader
                selectedSidebarItem={selectedSidebarItem}
                headerItems={headerItems}
                hasNotifications={hasNotifications}
                userAvatar={userAvatar}
                toggleNotifications={toggleNotifications}
                navigateToProfile={navigateToProfile}
                isOnProfilePage={location.pathname.includes('/dashboard/profil')}
                userCity={userCity}
                partnerInfo={partnerInfo}
            />

            {isMobileView && !isSidebarVisible && (
                <button className="burger-menu-button" onClick={toggleSidebar}>
                    <i className="pi pi-bars"></i>
                </button>
            )}

            <div className={`sidebar-mobile ${isSidebarVisible && isMobileView ? 'active' : ''}`}>
                <div className="mobile-heeder">
                    <Link to='/dashboard/home' className="logo-link">Frazza</Link>
                </div>

                <div className="mobile-sidebar-content">
                    <h3 className="sidebar-title">Navigation</h3>
                    {/* Render sidebar items and always show their header sub-tabs below each */}
                    {sidebarItems.map((sidebarItem, sIdx) => {
                        // Define header options for each section
                        let sectionHeaders = [];
                        if (getSidebarLabel(sidebarItem.icon) === 'Dates') {
                            sectionHeaders = [
                                { label: 'Cooking' },
                                { label: 'Movies' },
                                { label: 'Questions' },
                                { label: 'Minigames' }
                            ];
                        } else if (getSidebarLabel(sidebarItem.icon) === 'Organization') {
                            sectionHeaders = [
                                { label: 'Calendar' },
                                { label: 'ToDo List' },
                                { label: 'Notes' },
                                { label: 'Study' }
                            ];
                        } else if (getSidebarLabel(sidebarItem.icon) === 'Tracker') {
                            sectionHeaders = [
                                { label: 'Menstrual tracker' }
                            ];
                        }
                        return (
                            <div key={sIdx} className="sidebar-section-with-headers">
                                <nav
                                    className={sidebarItem.className}
                                    onClick={() => {
                                        sidebarItem.command();
                                        if (isMobileView) {
                                            setIsSidebarVisible(false);
                                        }
                                    }}
                                    role="menuitem"
                                    aria-label={getSidebarLabel(sidebarItem.icon)}
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            sidebarItem.command();
                                            if (isMobileView) {
                                                setIsSidebarVisible(false);
                                            }
                                        }
                                    }}
                                >
                                    <div className="sidebar-item-content">
                                        <i className={sidebarItem.icon} aria-hidden="true"></i>
                                        <span className="sidebar-label">{getSidebarLabel(sidebarItem.icon)}</span>
                                    </div>
                                    {sidebarItem.className.includes('active') && (
                                        <span className="active-indicator" aria-hidden="true"></span>
                                    )}
                                </nav>
                                {/* Always show header sub-tabs for this section */}
                                {sectionHeaders.length > 0 && (
                                    <ul className="mobile-header-subtabs-list">
                                        {sectionHeaders.map((header, hIdx) => {
                                            // If on Home, none are active
                                            const isHome = selectedSidebarItem === 'Home';
                                            const isActive = !isHome && header.label === selectedHeaderItem;
                                            return (
                                                <li
                                                    key={hIdx}
                                                    className={`mobile-header-subtab-listitem${isActive ? ' active' : ''}`}
                                                    style={{ color: isActive ? '#f5703d' : '#222' }}
                                                    onClick={() => {
                                                        setSelectedHeaderItem(header.label);
                                                        navigate(`/dashboard/${getSidebarLabel(sidebarItem.icon).toLowerCase()}/${header.label.toLowerCase().replace(/ /g, '-')}`);
                                                        setIsSidebarVisible(false);
                                                    }}
                                                    tabIndex={0}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            setSelectedHeaderItem(header.label);
                                                            navigate(`/dashboard/${getSidebarLabel(sidebarItem.icon).toLowerCase()}/${header.label.toLowerCase().replace(/ /g, '-')}`);
                                                            setIsSidebarVisible(false);
                                                        }
                                                    }}
                                                >
                                                    {header.label}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        );
                    })}
                    <div className="sidebar-section favorites-section">
                        <div className="favorites-header">
                            <h3 className="sidebar-title">Shortcuts</h3>
                            <Button 
                                icon="pi pi-plus" 
                                className="p-button-text p-button-rounded add-favorite-button" 
                                onClick={() => setShowAddFavoriteDialog(true)}
                                aria-label="Add favorite"
                            />
                        </div>
                        <div className="favorites-list">
                            {favorites.map((favorite, index) => (
                                <div key={index} className="favorite-item">
                                    <button 
                                        className="favorite-link"
                                        onClick={() => navigateToFavorite(favorite)}
                                    >
                                        {favorite.name}
                                    </button>
                                    <Button 
                                        icon="pi pi-times" 
                                        className="p-button-text p-button-rounded remove-favorite"
                                        onClick={() => handleRemoveFavorite(favorite)}
                                        aria-label="Remove favorite"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {!isMobileView && (
                <FullSidebar 
                    sidebarItems={sidebarItems}
                    favorites={favorites}
                    onAddFavorite={handleAddFavorite}
                    onRemoveFavorite={handleRemoveFavorite}
                    onNavigateToFavorite={navigateToFavorite}
                    showAddFavoriteDialog={showAddFavoriteDialog}
                    setShowAddFavoriteDialog={setShowAddFavoriteDialog}
                    newFavoriteName={newFavoriteName}
                    setNewFavoriteName={setNewFavoriteName}
                    isAddingFavorite={isAddingFavorite}
                />
            )}

            <div className={`sidebar-overlay ${isSidebarVisible && isMobileView ? 'active' : ''}`} onClick={closeSidebar} />

            <main className={`dashboard-main-content 
                ${!isSidebarVisible || isMobileView ? 'full-width' : ''} 
                ${selectedSidebarItem === 'Home' ? 'home-layout' : ''}
                ${location.pathname.includes('/dashboard/profil') ? 'profile-page' : ''}`} style={mainBackgroundStyle}>

                {selectedSidebarItem === 'Home' ? (
                    <Pager
                        pages={[
                            location.pathname.includes('/dashboard/') ? (
                                <Outlet key="page-1" />
                            ) : (
                                <DashboardContent
                                    key="page-1"
                                    selectedSidebarItem={selectedSidebarItem}
                                    selectedHeaderItem={selectedHeaderItem}
                                />
                            )
                        ]}
                    />
                ) : location.pathname.includes('/dashboard/') ? (
                    <Outlet />
                ) : (
                    <DashboardContent
                        selectedSidebarItem={selectedSidebarItem}
                        selectedHeaderItem={selectedHeaderItem}
                    />
                )}
            </main>

            {selectedSidebarItem === 'Home' && (
                <DashboardAside
                    userAvatar={userAvatar}
                    userName={userName}
                    userCity={userCity}
                    partnerInfo={partnerInfo}
                    navigateToProfile={navigateToProfile}
                />
            )}

            <Dialog 
                visible={showAddFavoriteDialog} 
                onHide={() => setShowAddFavoriteDialog(false)}
                header="Add to Favorites"
                modal
                className="add-favorite-dialog frazza-dialog-sm"
                footer={(
                    <>
                        <Button 
                            label="Cancel" 
                            icon="pi pi-times" 
                            onClick={() => setShowAddFavoriteDialog(false)} 
                            className="p-button-text"
                        />
                        <Button 
                            label="Add" 
                            icon="pi pi-check" 
                            onClick={handleAddFavorite} 
                            loading={isAddingFavorite}
                            disabled={!newFavoriteName.trim()}
                        />
                    </>
                )}
            >
                <div className="frazza-form-group">
                    <label htmlFor="favoriteName" className="frazza-form-label">Name this shortcut</label>
                    <InputText
                        id="favoriteName"
                        value={newFavoriteName}
                        onChange={(e) => setNewFavoriteName(e.target.value)}
                        placeholder="Enter a name for this page"
                        autoFocus
                        className="frazza-input"
                    />
                </div>
            </Dialog>
        </div>
    );
};

export default Dashboard;
